// src/components/EditVehicleForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, Storage } from 'aws-amplify';
import { getVehicle } from '../graphql/queries'; 
import { updateVehicle } from '../graphql/mutations';
import { FaSave, FaTimesCircle, FaEdit, FaCarSide } from 'react-icons/fa';

// Función auxiliar para obtener la URL de S3 (Copiar del Dashboard/Catalog)
const getS3ImageUrl = (imageKey) => {
    if (!imageKey) return 'placeholder.jpg';
    const BUCKET_NAME = "vimcarbrokerwebdf867b205942429a806078394c9ab27b0fd84-dev"; 
    const REGION = "us-east-1"; 
    return `https://${BUCKET_NAME}.s3.${REGION}.amazonaws.com/public/${imageKey}`;
};


const formStyles = {
    container: { padding: '20px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', maxWidth: '900px', margin: '20px auto' },
    grid: { display: 'grid', gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', gap: '20px' },
    inputGroup: { display: 'flex', flexDirection: 'column', marginBottom: '15px' },
    label: { marginBottom: '5px', fontWeight: 'bold' },
    input: { padding: '10px', border: '1px solid #ddd', borderRadius: '4px' },
    button: { padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', marginTop: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
    cancelButton: { backgroundColor: '#6c757d', marginLeft: '10px' },
    imagePreview: { width: '100px', height: '100px', objectFit: 'cover', margin: '5px', border: '1px solid #ddd' }
};

export default function EditVehicleForm({ onVehicleUpdated }) {
    const { id } = useParams(); // Obtiene el ID del vehículo desde la URL
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [imageFiles, setImageFiles] = useState([]);
    
    // 1. Cargar Datos del Vehículo
    useEffect(() => {
        const fetchVehicle = async () => {
            try {
                const result = await API.graphql({
                    query: getVehicle,
                    variables: { id: id },
                    authMode: 'AMAZON_COGNITO_USER_POOLS' // 🛑 Solo Admin debe ver esta página
                });
                
                const vehicle = result.data.getVehicle;
                if (!vehicle) {
                    setError('Vehículo no encontrado.');
                    setIsLoading(false);
                    return;
                }
                
                // Mapear los datos al estado del formulario
                setFormData({
                    id: vehicle.id,
                    vehicleOwnerId: vehicle.vehicleOwnerId,
                    year: vehicle.year.toString(), 
                    make: vehicle.make || '', 
                    model: vehicle.model || '', 
                    titleStatus: vehicle.titleStatus || 'CLEAN',
                    mileage: vehicle.mileage.toString(), 
                    price: vehicle.price.toString(),
                    description: vehicle.description || '', 
                    videoURL: vehicle.videoURL || '',
                    isFeatured: vehicle.isFeatured || false,
                    isCashDeal: vehicle.isCashDeal || false,
                    status: vehicle.status || 'FOR_SALE',
                    // Mantener las claves existentes por si no se suben nuevas
                    currentImageKey: vehicle.imageKey, 
                    currentAllImageKeys: vehicle.allImageKeys || [] 
                });
                
            } catch (err) {
                console.error("Error al cargar datos del vehículo:", err);
                setError('Error al cargar los datos. Verifique permisos o ID.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchVehicle();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };
    
    const handleImageChange = (e) => {
        setImageFiles(Array.from(e.target.files));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            let uploadedKeys = formData.currentAllImageKeys;
            let mainImageKey = formData.currentImageKey;
            
            // 2. Subir Nuevas Imágenes a S3 si hay archivos nuevos
            if (imageFiles.length > 0) {
                // Sobreescribir las claves existentes con las nuevas
                uploadedKeys = []; 
                for (const file of imageFiles) {
                    // Usar una estructura de key similar a la de PublishVehicleForm
                    const imageKey = `vehicles/${formData.vehicleOwnerId || 'admin'}/${Date.now()}_${file.name.replace(/ /g, "_")}`;
                    await Storage.put(imageKey, file, { level: 'public', contentType: file.type });
                    uploadedKeys.push(imageKey);
                }
                mainImageKey = uploadedKeys[0];
            }
            
            // 3. Crear el objeto de entrada para GraphQL (Solo con los campos modificables)
            const vehicleInput = {
                id: formData.id, // NECESARIO para la mutación UPDATE
                year: parseInt(formData.year) || 0, 
                make: formData.make,
                model: formData.model,
                titleStatus: formData.titleStatus,
                mileage: parseInt(formData.mileage) || 0,
                price: parseFloat(formData.price) || 0.0,
                description: formData.description,
                videoURL: formData.videoURL,
                isFeatured: formData.isFeatured,
                isCashDeal: formData.isCashDeal,
                status: formData.status,
                imageKey: mainImageKey, 
                allImageKeys: uploadedKeys, 
            };

            // 4. Llamar a la mutación GraphQL
            await API.graphql({
                query: updateVehicle,
                variables: { input: vehicleInput },
                authMode: 'AMAZON_COGNITO_USER_POOLS'
            });

            alert(`Vehículo ${formData.model} actualizado con éxito.`);
            navigate('/admin'); // Redirigir al inventario
            if (onVehicleUpdated) onVehicleUpdated();

        } catch (err) {
            console.error("Error al actualizar vehículo:", err);
            setError("Error al actualizar. Verifique el formato de los datos y los permisos (401).");
        } finally {
            setIsSubmitting(false);
        }
    };
    
    if (isLoading) return <div style={{textAlign: 'center', padding: '50px'}}><FaCarSide/> Cargando datos del vehículo...</div>;
    if (error && !formData) return <div style={{color: 'red', textAlign: 'center', padding: '50px'}}>{error}</div>;
    if (!formData) return <div style={{color: 'red', textAlign: 'center', padding: '50px'}}>Error inesperado: No hay datos de vehículo.</div>;


    return (
        <div style={formStyles.container}>
            <h2><FaEdit /> Editar Vehículo: {formData.make} {formData.model} (ID: {formData.id?.substring(0, 8)})</h2>
            
            {error && <div style={{ padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', margin: '15px 0' }}>{error}</div>}
            
            <form onSubmit={handleSubmit}>
                <div style={formStyles.grid}>
                    {/* Columna Izquierda */}
                    <div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Marca (Make):</label>
                            <input type="text" name="make" value={formData.make} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Modelo (Model):</label>
                            <input type="text" name="model" value={formData.model} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Año (Year):</label>
                            <input type="number" name="year" value={formData.year} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Millas (Mileage):</label>
                            <input type="number" name="mileage" value={formData.mileage} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Precio ($):</label>
                            <input type="number" name="price" value={formData.price} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Título:</label>
                            <select name="titleStatus" value={formData.titleStatus} onChange={handleChange} style={formStyles.input}>
                                <option value="CLEAN">CLEAN</option>
                                <option value="SALVAGE">SALVAGE</option>
                                <option value="REBUILT">REBUILT</option>
                            </select>
                        </div>
                    </div>
                    
                    {/* Columna Derecha */}
                    <div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Descripción:</label>
                            <textarea name="description" value={formData.description} onChange={handleChange} style={{ ...formStyles.input, height: '100px' }} required />
                        </div>
                        
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Fotos (Reemplazar todas):</label>
                            <input type="file" onChange={handleImageChange} multiple />
                            <small>{imageFiles.length > 0 ? `${imageFiles.length} nuevo(s) archivo(s) listo(s) para subir.` : 'Seleccione archivos si desea reemplazar las fotos existentes.'}</small>
                        </div>
                        
                        <div style={{ marginBottom: '15px' }}>
                            <label style={formStyles.label}>Fotos Actuales ({formData.currentAllImageKeys.length}):</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                {formData.currentAllImageKeys.slice(0, 3).map((key, index) => (
                                    <img key={index} src={getS3ImageUrl(key)} alt={`Vehículo ${index}`} style={formStyles.imagePreview} />
                                ))}
                                {formData.currentAllImageKeys.length > 3 && <span>... y {formData.currentAllImageKeys.length - 3} más</span>}
                            </div>
                        </div>

                        <div style={{...formStyles.inputGroup, flexDirection: 'row', gap: '20px'}}>
                            <label><input type="checkbox" name="isFeatured" checked={formData.isFeatured} onChange={handleChange} /> Destacado</label>
                            <label><input type="checkbox" name="isCashDeal" checked={formData.isCashDeal} onChange={handleChange} /> Cash Deal</label>
                            <label><input type="checkbox" 
                                name="status" 
                                checked={formData.status === 'SOLD'} 
                                onChange={(e) => setFormData(p => ({...p, status: e.target.checked ? 'SOLD' : 'FOR_SALE'}))} 
                            /> Vendido (Status: {formData.status})</label>
                        </div>
                    </div>
                </div>

                <button type="submit" disabled={isSubmitting} style={formStyles.button}>
                    <FaSave style={{marginRight: '8px'}}/> {isSubmitting ? 'Guardando Cambios...' : 'Guardar Cambios'}
                </button>
                <button type="button" onClick={() => navigate('/admin')} style={{...formStyles.button, ...formStyles.cancelButton}}>
                    <FaTimesCircle style={{marginRight: '8px'}}/> Cancelar
                </button>
            </form>
        </div>
    );
}